import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { FiUpload } from "react-icons/fi";
import { FormattedMessage, useIntl } from "react-intl";
import Modal from "../../Modal";
import FileUpload from "../../FileUpload";
import styles from "./styles.module.css";
import { COMMON_FUNCTIONALITY_OPTIONS } from "../useFileManagementData";
import FormField from "../../FormField";
import { useSelector } from "react-redux";
import axiosInstance from "../../../api/axios";
import useNotification from "../../../hooks/useNotification";
import { MdOpenInNew } from "react-icons/md";

const reqLink =
  "https://bayersi.service-now.com/sp?id=sc_cat_item&sys_id=0256e45c2b840b94abe5fa97d891bf2d&sysparm_category=19cc324e1bc9d05007afddb7ab4bcb1d";

const DEFAULT_TEMPLATE_LINK =
  "https://bayergroup.sharepoint.com/:f:/r/sites/Truman-TeamInternal/Shared%20Documents/Team%20Internal/Truman%20Bulk%20Upload%20Templates%20and%20Instructions?d=w9c323d3018ce44608775f925a0c6ac33&csf=1&web=1&e=U84A3z";

const FUNCTIONALITY_TEMPLATE_MAP = {
  "consent-collection": {
    name: "Template Files",
    href: DEFAULT_TEMPLATE_LINK,
  },
};

export const UploadForm = ({ onUploadSuccess }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const controller_code = useSelector((state) => state.controllers.controller);
  const customer_scope = useSelector((state) => state.controller.scope);
  const [selectedFunctionality, setSelectedFunctionality] = useState(
    COMMON_FUNCTIONALITY_OPTIONS[0].value,
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { errorToast, successToast } = useNotification();

  useEffect(() => {
    if (open && !selectedFunctionality) {
      setSelectedFunctionality(COMMON_FUNCTIONALITY_OPTIONS[0].value);
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    setSelectedFunctionality(null);
    setSelectedFile(null);
    setFileError("");
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedFunctionality || !selectedFile || fileError || submitting) {
      return;
    }

    try {
      setSubmitting(true);

      await uploadFileManagementFile({
        file: selectedFile,
        functionality: selectedFunctionality,
        controllerCode: controller_code,
        customerScope: customer_scope,
        sourceSystem: "TRUMAN_UI",
      });

      handleClose();

      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setFileError("");
  };

  const handleFileError = (errorMessage) => {
    setSelectedFile(null);
    setFileError(errorMessage);
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        const base64 = typeof result === "string" ? result.split(",")[1] : "";
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const uploadFileManagementFile = async ({
    file,
    functionality,
    sourceSystem,
    customerScope,
    controllerCode,
  }) => {
    try {
      const base64Content = await toBase64(file);

      const payload = {
        source_system: sourceSystem,
        functionality,
        extended_attributes: {
          customer_scope: customerScope,
          controller_code: controllerCode,
        },
        file: {
          name: file.name,
          content: base64Content,
        },
      };

      const response = await axiosInstance.post("/files", payload);

      const successMessage =
        response?.data?.data?.message || "File uploaded successfully";

      successToast(successMessage);
      return response;
    } catch (error) {
      const errorMessage =
        error?.data?.message || error?.message || "Failed to upload file";

      errorToast(errorMessage);
      throw error;
    }
  };

  const template = FUNCTIONALITY_TEMPLATE_MAP[selectedFunctionality] || {
    href: DEFAULT_TEMPLATE_LINK,
  };

  const confirmDisabled =
    !selectedFunctionality || !selectedFile || Boolean(fileError) || submitting;

  return (
    <>
      <button
        className="btn-primary"
        style={{ padding: "12px 17px", marginLeft: "auto" }}
        onClick={() => setOpen(true)}
      >
        <FiUpload />{" "}
        <FormattedMessage id="file-management.upload" defaultMessage="Upload" />
      </button>

      <Modal
        bodyStyle={styles.modalWrapper}
        open={open}
        setOpen={setOpen}
        onCancel={handleClose}
        handleClose={handleClose}
        title={intl.formatMessage({
          id: "file-management.upload.title",
          defaultMessage: "Upload File",
        })}
        okText={intl.formatMessage({
          id: "file-management.upload.confirm",
          defaultMessage: "Upload File",
        })}
        onSubmit={handleSubmit}
        confirmDisabled={confirmDisabled}
      >
        <FormField
          options={COMMON_FUNCTIONALITY_OPTIONS}
          value={selectedFunctionality}
          type="select"
          name="functionality"
          label={intl.formatMessage({
            id: "file-management.upload.functionality",
            defaultMessage: "Functionality",
          })}
          handleChange={(e) => setSelectedFunctionality(e.target.value)}
          rules={{
            required: true,
          }}
        />
        <a
          href={template?.href ?? "#"}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.downloadBtn}
        >
          <MdOpenInNew className={styles.icon} />
          Templates and Instructions
        </a>
        <FileUpload
          accept=".csv"
          allowedExtensions={[".csv"]}
          helperText={[
            "CSV files only, File must not exceed 4 MB.",
            <>
              For files larger than 4 MB, please raise a{" "}
              <a
                href={reqLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Service Request
              </a>
            </>,
          ]}
          error={fileError}
          onFileSelect={handleFileSelect}
          onError={handleFileError}
        />
      </Modal>
    </>
  );
};

UploadForm.propTypes = {
  onUploadSuccess: PropTypes.func,
};